/**
 * Formats variable names into user-friendly display names
 *
 * Handles:
 * - Specific mappings for known variables
 * - Section prefix removal (e.g., strategyAndExecution_timeManagement -> Time Management)
 * - CamelCase/PascalCase splitting
 * - Long name truncation
 */

// Specific mappings for known variables
const VARIABLE_MAPPINGS: Record<string, string> = {
  teamAdmitMistake: 'Team Admit Mistake',
  teamOverlapAirtimeTimesEvaluation: 'Team Overlap',
  teamOverlapAirtimeTimesExecution: 'Team Overlap',
  teamTimesTalkedTotal: 'Team Times Talked Total',
  teamTimesTalkedPreplanning: 'Team Times Talked Preplanning',
  teamTimesTalkedExecution: 'Team Times Talked Execution',
  teamTimesTalkedEvaluation: 'Team Times Talked Evaluation',
  teamAirtimePreplanning: 'Team Airtime Preplanning',
  timeManagement: 'Time Management',
  planning: 'Planning',
  evaluated: 'Evaluated',
  followPlan: 'Follow Plan'
}

/**
 * Splits camelCase or PascalCase string into words
 * Handles cases like:
 * - timeManagement -> time Management
 * - timeSuperManagement -> time Super Management
 * - SuperManagement -> Super Management
 */
const splitCamelCase = (str: string): string[] => {
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Insert space between lowercase and uppercase (e.g., timeManagement -> time Management)
    .replace(/([A-Z][a-z]+)([A-Z])/g, '$1 $2') // Insert space after capitalized word before next capital (e.g., SuperManagement -> Super Management)
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Insert space between consecutive uppercase letters (e.g., XMLParser -> XML Parser)
    .split(' ')
    .filter((word) => word.length > 0)
}

/**
 * Capitalizes first letter of each word
 */
const capitalizeWords = (words: string[]): string => {
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')
}

/**
 * Formats a variable name into a user-friendly display name
 *
 * @param variable - The variable name to format (e.g., "teamAdmitMistake", "strategyAndExecution_timeManagement")
 * @returns Formatted display name (e.g., "Team Admit Mistake", "Time Management")
 */
export const formatVariableName = (variable: string): string => {
  if (!variable || typeof variable !== 'string') {
    return variable
  }

  // Check for specific mappings first
  if (VARIABLE_MAPPINGS[variable]) {
    return VARIABLE_MAPPINGS[variable]
  }

  // Handle section prefixes (e.g., "strategyAndExecution_timeManagement" -> "timeManagement")
  if (variable.includes('_')) {
    const parts = variable.split('_')
    const lastPart = parts[parts.length - 1]

    // If last part is a known mapping, use it
    if (VARIABLE_MAPPINGS[lastPart]) {
      return VARIABLE_MAPPINGS[lastPart]
    }

    // Otherwise format the last part
    const words = splitCamelCase(lastPart)
    return capitalizeWords(words)
  }

  // Handle camelCase/PascalCase
  const words = splitCamelCase(variable)
  const formatted = capitalizeWords(words)

  // Truncate very long names (e.g., "Team Overlap Airtime Times Evaluation" -> "Team Overlap")
  if (formatted.length > 20) {
    const firstTwoWords = words.slice(0, 2)
    if (firstTwoWords.length >= 2) {
      return capitalizeWords(firstTwoWords)
    }
  }

  return formatted
}
